import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BarChart3,
  LifeBuoy,
  LogOut,
  Moon,
  Search,
  ShieldCheck,
  Sun,
  Ticket,
  type LucideIcon,
} from "lucide-react";
import { Suspense, useCallback, useId, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, Outlet, useMatches, useNavigate } from "react-router";

import { RoleBadges } from "../access/RoleBadges";
import { api } from "../api/client";
import type { Me } from "../api/types";
import { ME_QUERY_KEY, can, signOut, useMe } from "../auth/session";
import { ChatButton, ChatProvider } from "../chat/ChatDrawer";
import { useDismiss } from "../lib/hooks";
import { useMediaQuery } from "../lib/hotkeys";
import { useTheme } from "../lib/theme";
import { LiveProvider } from "../live/live";
import { Avatar } from "../ui/Avatar";
import { Button } from "../ui/Button";
import { KeyboardShortcuts } from "../ui/CommandPalette";
import { ToggleRow } from "../ui/Field";
import { Spinner } from "../ui/Spinner";
import { ToastProvider } from "../ui/Toast";
import { NotificationBell } from "./NotificationBell";
import "../search/search.css";
import "./shell.css";

interface Destination {
  to: string;
  label: string;
  icon: LucideIcon;
  end: boolean;
}

function destinations(me: Me): Destination[] {
  const all: (Destination & { show: boolean })[] = [
    // Support staff work from Tickets (their home goes there); the help page is for everyone else.
    { to: "/", label: "Help", icon: LifeBuoy, end: true, show: !can(me, "use_workspace") },
    // One page for your tickets and (for staff) the work queues.
    { to: "/tickets", label: can(me, "use_workspace") ? "Tickets" : "Your tickets", icon: Ticket, end: true, show: true },
    { to: "/reports", label: "Reports", icon: BarChart3, end: false, show: can(me, "view_reports") },
    { to: "/admin", label: "Admin", icon: ShieldCheck, end: false, show: can(me, "manage_people_and_roles") },
  ];
  return all.filter((d) => d.show);
}

export function AppShell() {
  const me = useMe();
  const links = destinations(me);
  // One destination needs no navigation.
  const showTabs = links.length > 1;
  // Data-heavy pages (queues, dashboard, admin) mark their route { wide: true } and get more room;
  // "staff" and "admin" make a page wide only for the people who see a big table there.
  const wide = useMatches().some((match) => {
    const want = (match.handle as { wide?: boolean | "staff" | "admin" } | undefined)?.wide;
    return want === true || (want === "staff" && can(me, "use_workspace")) || (want === "admin" && me.roles.includes("admin"));
  });

  return (
    <LiveProvider>
      <ToastProvider>
        <ChatProvider>
          <KeyboardShortcuts />
          <div className={showTabs ? "app has-tabs" : "app"}>
            <a className="skip-link" href="#main">
              Skip to content
            </a>
            <header className="topbar">
              <div className={wide ? "topbar-inner topbar-inner-wide" : "topbar-inner"}>
                <Link to="/" className="brand">
                  <img className="brand-mark" src="/aspect-mark.svg" alt="" width={36} height={36} />
                  <span className="brand-name">
                    <span className="brand-prefix">Aspect </span>Support Desk
                  </span>
                </Link>
                {showTabs ? (
                  // On phones this same list becomes the bottom tab bar (see shell.css).
                  <nav aria-label="Main" className="tabs">
                    <ul>
                      {links.map(({ to, label, icon: Icon, end }) => (
                        <li key={to}>
                          <NavLink to={to} end={end} className="tab">
                            <Icon size={18} aria-hidden="true" />
                            <span>{label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </nav>
                ) : null}
                <div className="topbar-actions">
                  <TopbarSearch />
                  <ThemeToggle />
                  <ChatButton />
                  <NotificationBell />
                  <ProfileMenu me={me} />
                </div>
              </div>
            </header>
            <main id="main" className={wide ? "board board-wide" : "board"} tabIndex={-1}>
              <Suspense
                fallback={
                  <div className="full-page-center">
                    <Spinner label="Loading" />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </main>
          </div>
        </ChatProvider>
      </ToastProvider>
    </LiveProvider>
  );
}


/** Search from anywhere: a box on wide screens, an icon link to the search page otherwise. */
function TopbarSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  function submit(event: FormEvent) {
    event.preventDefault();
    const text = q.trim();
    navigate(text ? `/search?q=${encodeURIComponent(text)}` : "/search");
    setQ("");
  }
  return (
    <>
      <form role="search" aria-label="Quick search" className="search topbar-search" onSubmit={submit}>
        <Search size={16} className="search-icon" aria-hidden="true" />
        <label htmlFor="topbar-search" className="visually-hidden">
          Search tickets
        </label>
        <input
          id="topbar-search"
          type="search"
          className="input"
          placeholder="Search tickets"
          maxLength={200}
          value={q}
          onChange={(event) => setQ(event.target.value)}
        />
      </form>
      <Link to="/search" className="icon-btn topbar-search-link" aria-label="Search tickets" data-tooltip="Search" data-tooltip-align="end">
        <Search size={20} aria-hidden="true" />
      </Link>
    </>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const next = theme === "dark" ? "light" : "dark";
  const label = `Switch to ${next} theme`;
  return (
    <button
      type="button"
      className="icon-btn topbar-theme"
      aria-label={label}
      data-tooltip={theme === "dark" ? "Light theme" : "Dark theme"}
      data-tooltip-align="end"
      onClick={() => setTheme(next)}
    >
      {theme === "dark" ? <Sun size={20} aria-hidden="true" /> : <Moon size={20} aria-hidden="true" />}
    </button>
  );
}

/** Below this width the top-bar theme switch is hidden (shell.css), so the account panel has it. */
const NO_TOPBAR_THEME = "(max-width: 900px)";

/** Theme choice in the account panel, only where the top bar has no switch. */
function ThemeRow() {
  const { theme, setTheme } = useTheme();
  return (
    <ToggleRow
      label="Dark theme"
      description="Follows your device until you choose."
      checked={theme === "dark"}
      onChange={(checked) => setTheme(checked ? "dark" : "light")}
    />
  );
}

/** The avatar button and its panel: who you are, your roles, alert preference, sign out. */
function ProfileMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const narrow = useMediaQuery(NO_TOPBAR_THEME);
  const panelId = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, wrapper, button);
  const queryClient = useQueryClient();
  const preferences = useMutation({
    mutationFn: (notify: boolean) =>
      api<Me>("/api/auth/me/preferences", { method: "PUT", body: { notify_on_replies: notify } }),
    onSuccess: (updated) => queryClient.setQueryData(ME_QUERY_KEY, updated),
  });

  return (
    <div className="profile" ref={wrapper}>
      <button
        ref={button}
        type="button"
        className="profile-button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Your account: ${me.display_name}`}
        data-tooltip="Your account"
        data-tooltip-align="end"
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar name={me.display_name} seed={me.id} />
      </button>
      {open ? (
        <div id={panelId} className="popover profile-panel">
          <div className="cell-person">
            <Avatar name={me.display_name} seed={me.id} />
            <div className="cell-person-text">
              <span className="cell-strong">{me.display_name}</span>
              <span className="cell-secondary">{me.email}</span>
            </div>
          </div>
          <RoleBadges roles={me.roles} />
          <div className="toggle-list">
            {narrow ? <ThemeRow /> : null}
            <ToggleRow
              label="Alerts about new replies"
              description="Email and Teams. Key alerts always send."
              checked={me.notify_on_replies}
              disabled={preferences.isPending}
              onChange={(checked) => preferences.mutate(checked)}
            />
          </div>
          {can(me, "use_workspace") ? (
            <Link to="/help" className="btn btn-ghost btn-block" onClick={close}>
              <LifeBuoy size={18} aria-hidden="true" />
              Help page
            </Link>
          ) : null}
          <Button variant="secondary" className="btn-block" onClick={() => void signOut()}>
            <LogOut size={18} aria-hidden="true" />
            Sign out
          </Button>
        </div>
      ) : null}
    </div>
  );
}
